import { useMediaQuery, useTheme } from "@mui/material";
import {
  GridActionsCellItem,
  useGridApiContext,
  useGridApiEventHandler,
} from "@mui/x-data-grid";
import * as React from "react";
import { useLanguage } from "../../hooks";
import { translate } from "../../utils/translate";
import PTIcon from "./PTIcon";
import PTTooltip from "./PTTooltip";
import TableCustomized from "./TableCustomized";

export default function PTDataGrid({
  columns = [],
  actions,
  onRowEnter,
  onRowLeave,
  onRowsSelectionHandler,
  onRowSelectionModelChange,
  totalElements,
  ...props
}) {
  const { currentLang } = useLanguage();
  const theme = useTheme();
  const [_totalElements, setTotalElements] = React.useState(0);

  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isMidium = useMediaQuery(theme.breakpoints.between("md", "lg"));
  const direction = currentLang === "ar" ? "rtl" : "ltr";
  const functionActions = actions?.map((action) => action?.onClick);

  React.useEffect(() => {
    if (totalElements !== undefined) {
      setTotalElements(totalElements);
    }
  }, [totalElements]);

  const tableCols = React.useMemo(
    () => [
      ...columns
        .filter(
          (column) =>
            (!isMobile || !column?.hideMobile) &&
            (!isMidium || !column?.hideMidium)
        )
        .map((column) => ({
          ...column,
          headerName: column.headerName ? translate(column.headerName) : "",
          headerAlign: direction === "rtl" ? "right" : "left",
        })),
      {
        field: "actions",
        headerName: "",
        sortable: false,
        minWidth: 20,
        renderCell: (params) => (
          <CustomButtonElement
            actions={actions}
            params={params}
            direction={direction}
            isMobile={isMobile}
          />
        ),
      },
    ],
    [columns, functionActions]
  );

  return (
    <TableCustomized
      onRowsSelectionHandler={onRowsSelectionHandler}
      onRowSelectionModelChanged={onRowSelectionModelChange}
      columns={tableCols}
      currentLang={currentLang}
      totalElements={_totalElements}
      {...props}
    />
  );
}

const CustomButtonElement = ({ actions, params, isMobile, direction }) => {
  const [rowHovered, setRowHovered] = React.useState(false);
  const [cellHeight, setCellHeight] = React.useState(0);
  const rowId = params.id;
  const apiRef = useGridApiContext();

  const calculateCellHeight = () => {
    const rowElement = apiRef?.current.getRowElement(rowId);
    setCellHeight(rowElement?.style.maxHeight);
  };

  React.useEffect(() => {
    if (apiRef?.current.getRowElement(rowId).matches(":hover"))
      setRowHovered(true);
    calculateCellHeight();
  }, []);

  const handleRowEnter = ({ id }) => id === rowId && setRowHovered(true);
  const handleRowLeave = ({ id }) => id === rowId && setRowHovered(false);

  useGridApiEventHandler(apiRef, "rowMouseEnter", handleRowEnter);
  useGridApiEventHandler(apiRef, "rowMouseLeave", handleRowLeave);

  return (
    <div
      style={{
        display: rowHovered ? "flex" : "none",
        justifyContent: "center",
        alignItems: "center",
        position: "absolute",
        [direction === 'rtl' ? 'left' : 'right']: "25px",
        height: cellHeight,
        backgroundColor: "#e8e8e8",
        ...(isMobile && { display: "flex", backgroundColor: "transparent" }),
      }}
    >
      {actions
  ?.filter((action) => !action?.isHidden?.(params?.row))
  ?.map((action, index) => {
    const isDisabled = action.disabled ? action.disabled(params?.row) : false;
    
    return (
      <PTTooltip title={action.tooltip}>
        <GridActionsCellItem
          {...action}
          disabled={isDisabled}
          icon={<PTIcon icon={action.icon} />}
          key={index}
          label={action.label}
          onClick={(event) => {
            event.stopPropagation();
            action.onClick?.(params?.row);
          }}
          sx={{
            "&:hover": {
              backgroundColor: "#CCC8C3",
            },
            ...action.sx,
          }}
        />
      </PTTooltip>
    );
  })}
    </div>
  );
};
